import React, { useState } from "react";
import {
  Box,
  Button,
  Typography,
  TextField,
  Grid,
  CircularProgress,
  Alert,
  Chip,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import ImageSearchIcon from "@mui/icons-material/ImageSearch";
import LanguageSelector from "./LanguageSelector";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { storage } from "./firebase";
import { marked } from "marked";

import { CheckCircleOutline } from "@mui/icons-material";
import { trackLesson } from "./functions";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";

const VisionSimple = ({ user, styles, theme, onAnalysisContentChange }) => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [prompt, setPrompt] = useState("");
  const [error, setError] = useState("");
  const [language, setLanguage] = useState("svenska");
  const [isDragging, setIsDragging] = useState(false);
  const [loading, setLoading] = useState(false);
  const [imageUrl, setImageUrl] = useState("");
  const [analysisSuccess, setAnalysisSuccess] = useState(false);
  const isDark = theme.palette.mode === "dark";
  const [showFullAnalysis, setShowFullAnalysis] = useState(false);
  const [analysisText, setAnalysisText] = useState("");

  const API_URL =
    process.env.NODE_ENV === "production"
      ? "/api"
      : "http://localhost:3001/api";

  const handleReset = () => {
    setSelectedImage(null);
    setPreviewUrl("");
    setAnalysisSuccess(false);
    setImageUrl("");
    setAnalysisText("");
    setShowFullAnalysis(false);
  };

  const getFormatPrompt = () => {
    const basePrompt = `Vad finns på bilden? Svara på ${language}. Förklara bildens innehåll steg för steg.`;
    return basePrompt + (prompt ? ` ${prompt}` : "");
  };

  // OPTION 2: Modified analyzeImage and handleImageChange functions

  // 1. Modify analyzeImage to accept a file parameter
  const analyzeImage = async (fileOverride) => {
    const fileToUse = fileOverride || selectedImage;

    if (!fileToUse) {
      setError("Välj en bild först.");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const imageRef = ref(
        storage,
        `visionImages/${user.uid}/${Date.now()}_${fileToUse.name}`,
      );
      await uploadBytes(imageRef, fileToUse);
      const uploadedImageUrl = await getDownloadURL(imageRef);
      setImageUrl(uploadedImageUrl);

      const reader = new FileReader();

      const base64Promise = new Promise((resolve, reject) => {
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(fileToUse);
      });

      const base64String = await base64Promise;
      const cleanBase64 = base64String
        .replace("data:image/jpeg;base64,", "")
        .replace("data:image/png;base64,", "")
        .replace("data:image/webp;base64,", "")
        .replace("data:image/gif;base64,", "");

      const response = await fetch(`${API_URL}/vision`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          base64Image: cleanBase64,
          prompt: getFormatPrompt(),
        }),
      });

      if (!response.ok) {
        const errorData = await response.text();
        throw new Error(`Server error: ${response.status} - ${errorData}`);
      }

      const data = await response.json();

      let analysisContent = data.description;
      if (analysisContent.startsWith("```html")) {
        analysisContent = analysisContent.slice(7, -3).trim();
      } else if (analysisContent.startsWith("```")) {
        analysisContent = analysisContent.slice(3, -3).trim();
      }

      // Call the callback function with the analysis result and image URL
      onAnalysisContentChange &&
        onAnalysisContentChange({
          analysis: analysisContent,
          imageUrl: uploadedImageUrl,
        });
      setAnalysisText(analysisContent);

      setAnalysisSuccess(true);

      await trackLesson(user.uid, "VisionSimple.js", null, {
        imageUrl: uploadedImageUrl,
        analysis: analysisContent,
      });
    } catch (err) {
      console.error("Error analyzing image:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // 2. Modify handleImageChange to pass file directly to analyzeImage
  // Modifiera handleImageChange för att lägga till HEIC-stöd
  const handleImageChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Check file size (5MB limit)
      if (file.size > 5 * 1024 * 1024) {
        setError("Bilden får inte vara större än 5MB");
        return;
      }

      // Utöka listan med tillåtna filtyper för att inkludera HEIC
      const allowedTypes = [
        "image/png",
        "image/jpeg",
        "image/webp",
        "image/gif",
        "image/heic",
        "image/heif",
      ];
      const allowedExtensions = [
        ".png",
        ".jpeg",
        ".jpg",
        ".webp",
        ".gif",
        ".heic",
        ".heif",
      ];

      // Check file extension
      const extension = "." + file.name.split(".").pop().toLowerCase();
      if (!allowedExtensions.includes(extension)) {
        setError(
          "Endast .png, .jpeg, .jpg, .webp, .gif och .heic-filer är tillåtna",
        );
        return;
      }

      // Hantera HEIC-filer
      if (extension === ".heic" || extension === ".heif") {
        setLoading(true);
        setError("");

        // Konvertera HEIC till JPEG med heic2any
        import("heic2any")
          .then((heic2anyModule) => {
            const heic2any = heic2anyModule.default || heic2anyModule;

            heic2any({
              blob: file,
              toType: "image/jpeg",
              quality: 0.8,
            })
              .then((convertedBlob) => {
                // Skapa en ny fil från bloben
                const jpegFile = new File(
                  [convertedBlob],
                  file.name.replace(/\.(heic|heif)$/i, ".jpg"),
                  { type: "image/jpeg" },
                );

                // Uppdatera UI med den konverterade bilden
                setSelectedImage(jpegFile);
                setPreviewUrl(URL.createObjectURL(jpegFile));
                setLoading(false);

                // Analysera den konverterade bilden
                analyzeImage(jpegFile);
              })
              .catch((err) => {
                console.error("Error converting HEIC:", err);
                setError(
                  "Kunde inte konvertera HEIC-filen. Försök med en annan bild.",
                );
                setLoading(false);
              });
          })
          .catch((err) => {
            console.error("Error loading heic2any:", err);
            setError(
              "Kunde inte ladda konverteringsbiblioteket. Försök med en annan bildtyp.",
            );
            setLoading(false);
          });

        return;
      }

      // För GIF-filer, kontrollera om de är animerade
      if (file.type === "image/gif") {
        const reader = new FileReader();
        reader.onload = (e) => {
          const arr = new Uint8Array(e.target.result);
          let frameCount = 0;

          // Check for multiple frames in GIF
          for (let i = 0; i < arr.length - 3; i++) {
            if (arr[i] === 0x00 && arr[i + 1] === 0x21 && arr[i + 2] === 0xf9) {
              frameCount++;
              if (frameCount > 1) {
                setError("Animerade GIF-filer stöds inte");
                return;
              }
            }
          }

          // If we get here, it's a non-animated GIF
          setSelectedImage(file);
          setPreviewUrl(URL.createObjectURL(file));
          setError("");

          // Pass the file directly to analyzeImage
          analyzeImage(file);
        };
        reader.readAsArrayBuffer(file);
      } else {
        // För andra bildformat, fortsätt som vanligt
        setSelectedImage(file);
        setPreviewUrl(URL.createObjectURL(file));
        setError("");

        // Pass the file directly to analyzeImage
        analyzeImage(file);
      }
    }
  };

  return (
    <Box sx={{ padding: "30px" }}>
      {loading ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "250px",
            width: "100%",
            position: "relative",
          }}
        >
          {/* Show the image being analyzed */}
          <Box
            sx={{
              width: "100%",
              maxWidth: "500px",
              margin: "0 auto",
              position: "relative",
              borderRadius: "12px",
              overflow: "hidden",
              aspectRatio: "4/3",
              // Subtle floating animation
              animation: "float 4s ease-in-out infinite",
              "@keyframes float": {
                "0%, 100%": {
                  transform: "translateY(0px) scale(1)",
                },
                "25%": {
                  transform: "translateY(-8px) scale(1.02)",
                },
                "50%": {
                  transform: "translateY(0px) scale(1.01)",
                },
                "75%": {
                  transform: "translateY(-4px) scale(1.015)",
                },
              },
            }}
          >
            <img
              src={previewUrl}
              alt=""
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                filter: "contrast(105%) brightness(90%)",
                // Subtle zoom animation
                animation: "imageZoom 6s ease-in-out infinite",
              }}
            />

            {/* Enhanced overlay with multiple effects */}
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: "rgba(0, 0, 0, 0.3)",
                overflow: "hidden",
              }}
            >
              {/* Original scanning line */}
              <Box
                sx={{
                  position: "absolute",
                  left: 0,
                  right: 0,
                  height: "2px",
                  backgroundColor: "transparent",
                  borderTop: `2px solid ${theme.palette.primary.main}`,
                  animation:
                    "bounce 2.5s cubic-bezier(0.42, 0, 0.58, 1) infinite",
                  "@keyframes bounce": {
                    "0%, 100%": { top: "1%" },
                    "50%": { top: "99%" },
                  },
                  zIndex: 3,
                }}
              />

              {/* Secondary scanning line (perpendicular) */}
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  bottom: 0,
                  width: "2px",
                  backgroundColor: "transparent",
                  borderLeft: `2px solid ${theme.palette.secondary.main}80`,
                  animation:
                    "horizontalScan 3s cubic-bezier(0.42, 0, 0.58, 1) infinite",
                  animationDelay: "1s",
                  "@keyframes horizontalScan": {
                    "0%, 100%": { left: "1%" },
                    "50%": { left: "99%" },
                  },
                  zIndex: 3,
                }}
              />

              {/* Animated grid overlay */}
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundImage:
                    "linear-gradient(rgba(255, 255, 255, 0.08) 1px, transparent 1px), linear-gradient(90deg, rgba(255, 255, 255, 0.08) 1px, transparent 1px)",
                  backgroundSize: "20px 20px",
                  animation: "gridShift 8s linear infinite",
                  "@keyframes gridShift": {
                    "0%": { backgroundPosition: "0 0" },
                    "100%": { backgroundPosition: "20px 20px" },
                  },
                  zIndex: 2,
                }}
              />

              {/* Corner brackets with glow effect */}
              {[
                {
                  top: "10px",
                  left: "10px",
                  borderTop: `2px solid ${theme.palette.primary.main}`,
                  borderLeft: `2px solid ${theme.palette.primary.main}`,
                },
                {
                  top: "10px",
                  right: "10px",
                  borderTop: `2px solid ${theme.palette.primary.main}`,
                  borderRight: `2px solid ${theme.palette.primary.main}`,
                },
                {
                  bottom: "10px",
                  left: "10px",
                  borderBottom: `2px solid ${theme.palette.primary.main}`,
                  borderLeft: `2px solid ${theme.palette.primary.main}`,
                },
                {
                  bottom: "10px",
                  right: "10px",
                  borderBottom: `2px solid ${theme.palette.primary.main}`,
                  borderRight: `2px solid ${theme.palette.primary.main}`,
                },
              ].map((style, index) => (
                <Box
                  key={index}
                  sx={{
                    position: "absolute",
                    width: "25px",
                    height: "25px",
                    ...style,
                    animation: `cornerGlow 2s ease-in-out infinite ${index * 0.5}s`,
                    "@keyframes cornerGlow": {
                      "0%, 100%": {
                        opacity: 0.8,
                        boxShadow: `0 0 5px ${theme.palette.primary.main}40`,
                      },
                      "50%": {
                        opacity: 1,
                        boxShadow: `0 0 15px ${theme.palette.primary.main}80`,
                      },
                    },
                    zIndex: 4,
                  }}
                />
              ))}

              {/* Enhanced pulsing targets with variety */}
              {[...Array(8)].map((_, index) => {
                const randomTop = Math.floor(Math.random() * 70) + 15;
                const randomLeft = Math.floor(Math.random() * 70) + 15;
                const size = 15 + (index % 3) * 8; // Varying sizes
                const color =
                  index % 2 === 0
                    ? theme.palette.primary.main
                    : theme.palette.secondary.main;

                return (
                  <Box
                    key={`target-${index}`}
                    sx={{
                      position: "absolute",
                      top: `${randomTop}%`,
                      left: `${randomLeft}%`,
                      width: `${size}px`,
                      height: `${size}px`,
                      borderRadius: "50%",
                      border: `1px solid ${color}80`,
                      animation: `targetPulse 2.5s ease-out infinite ${index * 0.3}s`,
                      "@keyframes targetPulse": {
                        "0%": {
                          transform: "scale(0)",
                          opacity: 1,
                          borderWidth: "2px",
                        },
                        "50%": {
                          opacity: 0.8,
                          borderWidth: "1px",
                        },
                        "100%": {
                          transform: "scale(3)",
                          opacity: 0,
                          borderWidth: "0px",
                        },
                      },
                      zIndex: 3,
                    }}
                  />
                );
              })}

              {/* Diagonal scanning lines */}
              <Box
                sx={{
                  position: "absolute",
                  top: "-50%",
                  left: "-50%",
                  right: "-50%",
                  bottom: "-50%",
                  background: `repeating-linear-gradient(
              45deg,
              transparent,
              transparent 40px,
              ${theme.palette.primary.main}15 40px,
              ${theme.palette.primary.main}15 42px
            )`,
                  animation: "diagonalMove 4s linear infinite",
                  "@keyframes diagonalMove": {
                    "0%": { transform: "translate(-100px, -100px)" },
                    "100%": { transform: "translate(100px, 100px)" },
                  },
                  zIndex: 1,
                }}
              />
            </Box>

            {/* Add keyframes for image zoom */}
            <style jsx>{`
              @keyframes imageZoom {
                0%,
                100% {
                  transform: scale(1);
                }
                25% {
                  transform: scale(1.05);
                }
                50% {
                  transform: scale(1.02);
                }
                75% {
                  transform: scale(1.08);
                }
              }
            `}</style>
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              mt: 3,
              justifyContent: "center",
            }}
          >
            <CircularProgress size={20} />
            <Typography sx={{ fontWeight: 600 }}>
              Analyserar bilden — tar 10-15 sekunder...
            </Typography>
          </Box>
        </Box>
      ) : (
        <>
          {analysisSuccess ? (
            <Box sx={{ ...styles?.successBox, pt: 4, pb: 4 }}>
              <CheckCircleOutline
                sx={{ fontSize: 60, color: "success.main", mb: 3 }}
              />
              <Typography
                variant="h6"
                sx={{
                  color: "success.main",
                  fontWeight: 500,
                  textAlign: "center",
                  width: "80%",
                  margin: "0 auto",
                  mb: 3,
                }}
              >
                Bilden är nu analyserad och du kan nu fortsätta med genereringen
                med detta som underlag.
              </Typography>

              {/* Image and Analysis Preview Box */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  width: "100%",
                  mb: 3,
                  p: 2,
                  borderRadius: 4,
                }}
              >
                {/* Image preview */}
                <Box sx={{ mb: 2, textAlign: "center", display: "none" }}>
                  <img
                    src={imageUrl}
                    alt="Analyzed image"
                    style={{
                      maxWidth: "100%",
                      maxHeight: "150px",
                      borderRadius: "8px",
                      objectFit: "contain",
                    }}
                  />
                </Box>

                {/* Analysis text preview with expand/collapse */}
                <Box
                  onClick={() => setShowFullAnalysis(!showFullAnalysis)}
                  sx={{
                    cursor: "pointer",
                    p: 2,
                    borderRadius: 4,
                    bgcolor: "white",
                    "&:hover": { bgcolor: "rgba(0, 0, 0, 0.02)" },
                  }}
                >
                  <Typography
                    component="div"
                    sx={{
                      "& h2, & h3": { margin: "16px 0 8px", padding: 0 },
                      "& ul, & ol": {
                        paddingLeft: "20px",
                        marginBottom: "16px",
                      },
                      "& li": { marginBottom: "8px" },
                      "& p": { marginBottom: "16px" },
                    }}
                  >
                    <span
                      dangerouslySetInnerHTML={{
                        __html: marked(
                          showFullAnalysis
                            ? analysisText
                            : `${analysisText.substring(0, 150)}${analysisText.length > 150 ? "..." : ""}`,
                        ),
                      }}
                    />
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      display: "block",
                      textAlign: "center",
                      color: "primary.main",
                      mt: 1,
                      cursor: "pointer",
                    }}
                    onClick={() => setShowFullAnalysis(!showFullAnalysis)}
                  >
                    {showFullAnalysis
                      ? "Klicka för att minimera"
                      : "Klicka för att visa mer"}
                  </Typography>
                </Box>
              </Box>

              <Button
                variant="purp"
                onClick={handleReset}
                startIcon={<ArrowBackIosIcon />}
              >
                Välj annan bild
              </Button>
            </Box>
          ) : (
            <>
              <Box sx={{ width: "100%", maxWidth: "800px", mx: "auto", p: 0 }}>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                  <label
                    htmlFor="image-upload"
                    style={{ cursor: "pointer", width: "100%" }}
                    onDragEnter={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setIsDragging(true);
                    }}
                    onDragOver={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setIsDragging(true);
                    }}
                    onDragLeave={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      const rect = e.currentTarget.getBoundingClientRect();
                      if (
                        e.clientX < rect.left ||
                        e.clientX >= rect.right ||
                        e.clientY < rect.top ||
                        e.clientY >= rect.bottom
                      ) {
                        setIsDragging(false);
                      }
                    }}
                    onDrop={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setIsDragging(false);
                      const file = e.dataTransfer.files[0];
                      if (file)
                        handleImageChange({ target: { files: [file] } });
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        minHeight: "300px",
                        width: "100%",
                        borderRadius: "8px",
                        backgroundColor: isDragging
                          ? isDark
                            ? "rgba(107, 33, 168, 0.1)"
                            : "rgba(237, 233, 254, 0.5)"
                          : isDark
                            ? "rgba(88, 28, 135, 0.08)"
                            : "rgba(245, 243, 255, 0.3)",
                        border: "2px dashed",
                        borderColor: isDragging
                          ? isDark
                            ? "rgba(139, 92, 246, 0.6)"
                            : "rgba(109, 40, 217, 0.5)"
                          : isDark
                            ? "rgba(139, 92, 246, 0.35)"
                            : "rgba(167, 139, 250, 0.4)",
                        boxShadow: isDragging
                          ? "0 2px 6px rgba(139, 92, 246, 0.15)"
                          : "",
                        transition: "all 0.2s ease",
                        "&:hover": {
                          borderColor: isDark
                            ? "rgba(124, 58, 237, 0.6)"
                            : "rgba(109, 40, 217, 0.5)",
                          backgroundColor: isDark
                            ? "rgba(107, 33, 168, 0.1)"
                            : "rgba(237, 233, 254, 0.4)",
                          boxShadow: "0 3px 8px rgba(139, 92, 246, 0.12)",
                        },
                      }}
                    >
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageChange}
                        style={{ display: "none" }}
                        id="image-upload"
                      />
                      {selectedImage ? (
                        <Box sx={{ textAlign: "center" }}>
                          <Box sx={{ mb: 2 }}>
                            <img
                              src={previewUrl}
                              alt="Preview"
                              style={{
                                maxWidth: "100%",
                                maxHeight: "200px",
                                borderRadius: "8px",
                                objectFit: "contain",
                              }}
                            />
                          </Box>
                          <Typography
                            variant="body2"
                            color="success.main"
                            sx={{ fontWeight: "600", fontSize: "1.1rem" }}
                          >
                            "{selectedImage.name}" uppladdad och är redo att
                            analyseras!
                          </Typography>
                          <Button
                            onClick={(e) => {
                              e.preventDefault();
                              setSelectedImage(null);
                              setPreviewUrl("");
                              document.getElementById("image-upload").click();
                            }}
                            variant="purp"
                            startIcon={<CloudUploadIcon />}
                            sx={{
                              ...(styles?.tightButton || {}),
                              mt: 3,
                            }}
                          >
                            Välj en annan bild
                          </Button>
                        </Box>
                      ) : (
                        <Box sx={{ textAlign: "center" }}>
                          <CloudUploadIcon
                            sx={{
                              fontSize: 60,
                              color: isDragging
                                ? theme.palette.primary.main
                                : theme.palette.primary.main,
                              mb: 3,
                              transform: isDragging ? "scale(1.1)" : "scale(1)",
                              transition: "transform 0.2s ease-in-out",
                            }}
                          />
                          <Typography
                            variant="h6"
                            sx={{ mb: 1, fontWeight: isDragging ? 700 : 500 }}
                          >
                            {isDragging
                              ? "Släpp bilden här"
                              : "Dra och släpp bild här"}
                          </Typography>

                          <Button
                            variant="purp"
                            startIcon={<CloudUploadIcon />}
                            component="label"
                            htmlFor="image-upload"
                            sx={{
                              ...(styles?.tightButton || {}),
                              mt: 3,
                            }}
                          >
                            Ladda upp bild
                          </Button>
                        </Box>
                      )}
                    </Box>
                  </label>
                  {error && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                      {error}
                    </Alert>
                  )}
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      display: "block",
                      textAlign: "center",
                      mt: 0,
                    }}
                  >
                    Stödda format: PNG, JPEG, HEIC & WEBP. Max storlek: 5 MB
                  </Typography>
                  <Grid container spacing={2} sx={{ mt: 1, display: "none" }}>
                    <Grid item xs={12} md={6}>
                      <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: "500", mt: 0 }}
                      >
                        Jag vill ha analysen på:
                      </Typography>
                      <Box sx={{ mt: 1, mb: 2 }}>
                        <LanguageSelector
                          language={language}
                          setLanguage={setLanguage}
                        />
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: "500", mt: 0 }}
                      >
                        Ytterligare instruktioner (valfritt):
                      </Typography>
                      <Box sx={{ mt: 1, mb: 2 }}>
                        <TextField
                          fullWidth
                          placeholder="t.ex. förklara så en 5-åring förstår"
                          variant="outlined"
                          value={prompt}
                          onChange={(e) => setPrompt(e.target.value)}
                        />
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              </Box>

              <Button
                fullWidth
                variant="purp"
                onClick={analyzeImage}
                disabled={!selectedImage || loading}
                startIcon={
                  loading ? (
                    <CircularProgress sx={{ color: "white" }} size={20} />
                  ) : (
                    <CloudUploadIcon />
                  )
                }
                sx={{ mt: 3, display: "none" }}
              >
                {loading
                  ? "Analyserar bilden — tar 30-60 sekunder..."
                  : "Analysera bild"}
              </Button>
            </>
          )}
        </>
      )}
    </Box>
  );
};

export default VisionSimple;
